import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import type { SpotifyPlaylistDetails } from "../../types/outreach";

interface SpotifyPlaylistDetailProps {
  playlist: SpotifyPlaylistDetails | null;
  loading: boolean;
  onAdd: () => void;
  disableAdd?: boolean;
  disableReason?: string;
}

const formatCount = (value?: number): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "-";
  }

  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }

  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return value.toLocaleString();
};

const stripHtml = (value?: string): string => {
  if (!value) {
    return "";
  }

  return value.replace(/<[^>]*>/g, "");
};

export const SpotifyPlaylistDetail: React.FC<SpotifyPlaylistDetailProps> = ({
  playlist,
  loading,
  onAdd,
  disableAdd,
  disableReason,
}) => {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const description = useMemo(() => stripHtml(playlist?.description), [
    playlist?.description,
  ]);

  const displayDescription = useMemo(() => {
    if (!description) {
      return "";
    }

    if (showFullDescription || description.length <= 240) {
      return description;
    }

    return `${description.slice(0, 240)}...`;
  }, [description, showFullDescription]);

  const playlistImage = playlist?.images?.[0]?.url;
  const followerCount = playlist?.followers?.total;
  const trackCount = playlist?.tracks?.total;

  const addButton = (
    <Button
      variant="contained"
      color="primary"
      fullWidth
      startIcon={<LibraryAddIcon />}
      onClick={onAdd}
      disabled={disableAdd || !playlist}
    >
      {disableAdd ? "Playlist Added" : "Add to Spotify Playlists"}
    </Button>
  );

  if (loading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={160} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rectangular" height={32} />
            <Skeleton variant="rectangular" height={48} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!playlist) {
    return (
      <Card
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CardContent>
          <Typography variant="body1" color="text.secondary" align="center">
            Select a playlist to view details.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const actionButton = disableReason ? (
    <Tooltip title={disableReason}>{addButton}</Tooltip>
  ) : (
    addButton
  );

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {playlistImage ? (
        <CardMedia
          component="img"
          src={playlistImage}
          alt={playlist.name}
          sx={{ width: 160, height: 160, mx: "auto", mt: 3, borderRadius: 2 }}
        />
      ) : (
        <Box
          sx={{
            width: 160,
            height: 160,
            mx: "auto",
            mt: 3,
            borderRadius: 2,
            bgcolor: "background.default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            No Image
          </Typography>
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Stack spacing={2} flexGrow={1}>
          <Box>
            <Typography variant="h6" gutterBottom>
              <Button
                component="a"
                href={playlist.external_urls?.spotify}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  justifyContent: "flex-start",
                  px: 0,
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {playlist.name}
              </Button>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Curated by {playlist.owner?.display_name ?? "Unknown"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={playlist.public ? "Public" : "Private"}
              color={playlist.public ? "success" : "default"}
              size="small"
            />
            {playlist.collaborative && (
              <Chip label="Collaborative" color="primary" size="small" />
            )}
          </Stack>

          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Followers
              </Typography>
              <Typography variant="h6">{formatCount(followerCount)}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Tracks
              </Typography>
              <Typography variant="h6">{formatCount(trackCount)}</Typography>
            </Box>
          </Stack>

          {description && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-wrap" }}>
                {displayDescription || "No description provided."}
              </Typography>
              {description.length > 240 && (
                <Button
                  size="small"
                  onClick={() => setShowFullDescription((prev) => !prev)}
                  sx={{ mt: 1, px: 0 }}
                >
                  {showFullDescription ? "Show less" : "Show more"}
                </Button>
              )}
            </Box>
          )}

          <Box mt="auto">{actionButton}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
